/**
 * Modèle 3D dans la vidéo (style Observation et bloc 3D des autres styles) : le modèle tourne lentement ; quand la voix
 * nomme un point d'intérêt, le modèle pivote pour le présenter de face, s'approche, et l'étiquette apparaît avec son
 * explication. Tout est une fonction de l'image courante : même résultat dans l'aperçu du studio et au rendu.
 * Caméra fixe (même cadrage que l'app) ; c'est le modèle qui bouge : plus simple et parfaitement déterministe.
 * Au repos, le modèle se balance doucement autour de la vue de l'app (celle où l'auteur a posé ses points, par exemple
 * la coupe ouverte d'une cellule) ; sur un point visé, il revient à cette vue, s'approche et amène le point au centre.
 */
import { ThreeCanvas } from "@remotion/three";
import { useThree } from "@react-three/fiber";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { continueRender, delayRender } from "remotion";
import { Box3, Euler, Matrix4, PerspectiveCamera, Quaternion, Vector3, type Material, type Mesh, type Object3D } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { FPS, type Hotspot, type ModelState } from "@jange/shared";
import type { Theme } from "../styles.js";

type Vec = [number, number, number];
const FOV = 40;
const CAM: Vec = [0.9, 0.5, 2.6]; // × rayon du modèle, comme dans l'app
const LIGHT_A: Vec = [3, 5, 4];
const LIGHT_B: Vec = [-4, -2, -3];
const SWAY_FRAMES = 24 * FPS; // balancement complet en 24 s, au repos
const SWAY = 0.28; // ± 16° autour de la vue de l'app
const FOCUS_FRAMES = Math.round(1.2 * FPS);
const FOCUS_SCALE = 1.5;
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

const cache = new Map<string, Promise<Object3D>>();
function loadGlb(url: string) {
  let p = cache.get(url);
  if (!p) {
    p = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(url).then((g) => g.scene);
    cache.set(url, p);
  }
  return p;
}

const ROOT_NAMES = new Set(["", "modèle", "Scene", "scene", "RootNode", "root"]);
const nameOf = (o: Object3D | null | undefined) => (o ? ((o.userData?.name as string | undefined) ?? o.name) : "");
const partIdOf = (m: Object3D) => (ROOT_NAMES.has(nameOf(m)) ? (ROOT_NAMES.has(nameOf(m.parent)) ? "" : nameOf(m.parent)) : nameOf(m));
const DIM_OPACITY = 0.14;
type Original = { transparent: boolean; opacity: number; depthWrite: boolean };
/** Même règle que l'app : les autres parties s'estompent, la transparence d'origine du modèle est conservée. */
function setDimmed(mat: Material, dim: boolean) {
  const o = (mat.userData.jangeOriginal ??= { transparent: mat.transparent, opacity: mat.opacity, depthWrite: mat.depthWrite }) as Original;
  mat.transparent = dim || o.transparent;
  mat.opacity = dim ? Math.min(o.opacity, DIM_OPACITY) : o.opacity;
  mat.depthWrite = dim ? false : o.depthWrite;
  mat.needsUpdate = true;
}

type Loaded = { scene: Object3D; radius: number; offset: Vec; meshes: { mesh: Mesh; part: string }[] };
/** Charge le modèle (le rendu attend), le centre, et garde une copie propre des matériaux. */
function useModel(url: string | null): Loaded | null {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  useEffect(() => {
    if (!url) return;
    const handle = delayRender(`modèle 3D ${url}`, { timeoutInMilliseconds: 120_000 });
    let alive = true;
    loadGlb(url).then((cached) => {
      if (!alive) return;
      const scene = cached.clone(true);
      const meshes: Loaded["meshes"] = [];
      scene.traverse((o) => {
        const m = o as Mesh;
        if (!m.isMesh) return;
        m.material = Array.isArray(m.material) ? m.material.map((x) => x.clone()) : (m.material as Material).clone();
        meshes.push({ mesh: m, part: partIdOf(m) });
      });
      const box = new Box3().setFromObject(scene);
      const c = box.getCenter(new Vector3());
      setLoaded({ scene, radius: box.getSize(new Vector3()).length() / 2 || 1, offset: [-c.x, -c.y, -c.z], meshes });
    }).catch(() => { /* modèle illisible : la vidéo montre le bloc sans 3D */ }).finally(() => continueRender(handle));
    return () => { alive = false; };
  }, [url]);
  return loaded;
}

/** Pose du modèle à une image : lacet, échelle, point local amené au centre ; enchaîne les points d'intérêt. */
function poseAt(state: ModelState, hotspots: Hotspot[], offset: Vec, frame: number): { yaw: number; scale: number; q: Vector3; k: number } {
  const shown = state.shownAt ?? frame;
  const sway = (f: number) => SWAY * Math.sin(((f - shown) * 2 * Math.PI) / SWAY_FRAMES);
  let yaw = sway(frame);
  let scale = 1, q = new Vector3(), k = 0;
  let prev = { yaw: 0, scale: 1, q: new Vector3() };
  for (const ev of state.history) {
    if (ev.at > frame) break;
    const h = hotspots.find((x) => x.id === ev.hotspotId);
    if (!h) continue;
    // Pose au départ du mouvement : celle d'avant (balancement libre ou point précédent).
    const startYaw = prev.q.lengthSq() ? prev.yaw : sway(ev.at);
    const local = new Vector3(...h.position).add(new Vector3(...offset));
    const target = 0; // vue de l'app : les points y sont visibles (coupe, face utile)
    const kk = easeInOut(clamp01((frame - ev.at) / FOCUS_FRAMES));
    yaw = startYaw + (target - startYaw) * kk;
    scale = prev.scale + (FOCUS_SCALE - prev.scale) * kk;
    q = prev.q.clone().lerp(local, kk);
    k = kk;
    prev = { yaw: target, scale: FOCUS_SCALE, q: local };
  }
  return { yaw, scale, q, k };
}

function LookAtOrigin() {
  const { camera } = useThree();
  useLayoutEffect(() => { camera.lookAt(0, 0, 0); camera.updateProjectionMatrix(); }, [camera]);
  return null;
}

/** Mise en avant d'une partie : les autres s'estompent (même règle que l'app). */
function Highlight({ loaded, part }: { loaded: Loaded; part: string | null }) {
  const { invalidate } = useThree();
  useLayoutEffect(() => {
    for (const { mesh, part: id } of loaded.meshes) {
      const dim = !!part && id !== part;
      for (const mat of (Array.isArray(mesh.material) ? mesh.material : [mesh.material]) as Material[]) setDimmed(mat, dim);
      mesh.renderOrder = dim ? 1 : 0;
    }
    invalidate();
  }, [loaded, part, invalidate]);
  return null;
}

export function Model({ url, hotspots, state, frame, width, height, lang, theme, credit }: { url: string | null; hotspots: Hotspot[]; state: ModelState; frame: number; width: number; height: number; lang: string; theme: Theme; credit?: string | null }) {
  const loaded = useModel(url);
  const appear = state.shownAt === null ? 0 : easeOut(clamp01((frame - state.shownAt) / (0.6 * FPS)));
  const pose = loaded ? poseAt(state, hotspots, loaded.offset, frame) : null;
  const r = loaded?.radius ?? 1;
  const camPos: Vec = [CAM[0] * r, CAM[1] * r, CAM[2] * r];
  // Transformation du modèle : T · S · R(lacet) · (p + offset), le point visé ramené vers le centre.
  const matrix = useMemo(() => {
    if (!pose) return new Matrix4();
    const rot = new Quaternion().setFromEuler(new Euler(0, pose.yaw, 0));
    const s = pose.scale * (0.92 + 0.08 * appear);
    const centered = pose.q.clone().applyQuaternion(rot).multiplyScalar(s * 0.9 * pose.k);
    return new Matrix4().compose(centered.negate(), rot, new Vector3(s, s, s));
  }, [pose?.yaw, pose?.scale, pose?.k, pose?.q.x, pose?.q.y, pose?.q.z, appear]); // eslint-disable-line react-hooks/exhaustive-deps
  const position = useMemo(() => new Vector3().setFromMatrixPosition(matrix), [matrix]);
  // Étiquettes : projection des points avec une caméra identique à celle du canevas.
  const labels = useMemo(() => {
    if (!loaded || !pose) return [];
    const cam = new PerspectiveCamera(FOV, width / height, Math.max(1e-4, r / 100), Math.max(100, r * 40));
    cam.position.set(...camPos); cam.lookAt(0, 0, 0); cam.updateMatrixWorld(true);
    const rot = new Quaternion().setFromEuler(new Euler(0, pose.yaw, 0));
    return state.named.flatMap((n) => {
      const h = hotspots.find((x) => x.id === n.hotspotId);
      if (!h) return [];
      const world = new Vector3(...h.position).add(new Vector3(...loaded.offset)).applyMatrix4(matrix);
      const toCam = new Vector3(...camPos).sub(world).normalize();
      const facing = h.normal ? new Vector3(...h.normal).applyQuaternion(rot).dot(toCam) : 1;
      const v = world.clone().project(cam);
      if (v.z > 1) return [];
      const k = easeOut(clamp01((frame - n.at) / (0.4 * FPS)));
      const x = ((v.x + 1) / 2) * width;
      return [{ id: h.id, x, y: ((1 - v.y) / 2) * height, label: h.label[lang] ?? h.label.fr ?? h.id, text: h.text?.[lang] ?? h.text?.fr ?? "", focused: state.focus?.hotspotId === h.id, visible: facing > -0.35, k, left: x > width * 0.58 }];
    });
  }, [loaded, pose, state.named, state.focus, matrix, hotspots, lang, width, height, frame, r]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: "relative", width, height, borderRadius: theme.rough ? 0 : 22, overflow: "hidden", background: theme.id === "observation" ? "transparent" : theme.id === "tableau" ? "rgba(0,0,0,0.18)" : "#F6F7F9", opacity: appear }}>
      {loaded && pose && (
        <ThreeCanvas width={width} height={height} dpr={1} gl={{ antialias: true }} camera={{ fov: FOV, position: camPos, near: Math.max(1e-4, r / 100), far: Math.max(100, r * 40) }}>
          <LookAtOrigin />
          <ambientLight intensity={0.9} />
          <directionalLight position={LIGHT_A} intensity={1.2} />
          <directionalLight position={LIGHT_B} intensity={0.35} />
          <group position={position} quaternion={new Quaternion().setFromRotationMatrix(matrix)} scale={new Vector3().setFromMatrixScale(matrix)}>
            <group position={loaded.offset}><primitive object={loaded.scene} /></group>
          </group>
          <Highlight loaded={loaded} part={state.part?.partId ?? null} />
        </ThreeCanvas>
      )}
      {!loaded && url && <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: theme.muted, fontSize: 24, fontFamily: theme.fontFamily }}>Modèle 3D…</div>}
      {/* Étiquettes : le point visé avec son explication, les autres points nommés en rappel. */}
      <svg width={width} height={height} style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        {labels.filter((l) => l.visible).map((l) => (
          <g key={l.id} opacity={l.k}>
            {l.focused && <line x1={l.x} y1={l.y} x2={l.x + (l.left ? -26 : 26)} y2={l.y - 26} stroke={theme.amber} strokeWidth={3} />}
            <circle cx={l.x} cy={l.y} r={l.focused ? 11 : 7} fill={l.focused ? theme.amber : theme.accent} stroke="#fff" strokeWidth={3} />
          </g>
        ))}
      </svg>
      {labels.filter((l) => l.visible).map((l) => (
        <div key={l.id} style={{ position: "absolute", ...(l.focused && l.left ? { right: width - l.x + 30 } : { left: Math.min(width - (l.focused ? 440 : 220), l.x + (l.focused ? 30 : 14)) }), top: Math.max(8, l.y - (l.focused ? 70 : 18)), opacity: l.k, transform: `translateY(${(1 - l.k) * 8}px)`, fontFamily: theme.fontFamily, pointerEvents: "none" }}>
          {l.focused
            ? <div style={{ maxWidth: 420, background: "rgba(8,12,24,0.86)", color: "#fff", borderRadius: 16, padding: "12px 16px", borderLeft: `5px solid ${theme.amber}` }}>
                <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.1 }}>{l.label}</div>
                {l.text && <div style={{ fontSize: 22, lineHeight: 1.35, marginTop: 6, color: "#DDE3EE" }}>{l.text}</div>}
              </div>
            : <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", background: "rgba(8,12,24,0.7)", padding: "4px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>{l.label}</div>}
        </div>
      ))}
      {credit && <div style={{ position: "absolute", right: 14, top: 10, fontSize: 16, color: theme.id === "presentation" ? "#5B6478" : theme.muted, fontFamily: theme.fontFamily, opacity: 0.9 }}>{credit}</div>}
    </div>
  );
}
